import { Phone, Mail, MapPin, Instagram, Facebook, Linkedin } from "lucide-react";
import logo from "@/assets/logo.jpeg";
import { CONTACT } from "@/data/content";
import { useLanguage } from "@/context/LanguageContext";

const Footer = () => {
  const { t } = useLanguage();

  const navLinks = [
    { label: t("nav.about"), id: "about" },
    { label: t("nav.story"), id: "story" },
    { label: t("nav.products"), id: "products" },
    { label: t("nav.markets"), id: "markets" },
    { label: t("nav.values"), id: "values" },
    { label: t("nav.faq"), id: "faq" },
    { label: t("nav.contact"), id: "contact" },
  ];

  const go = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer className="bg-white border-t border-[#40916c]/10 pt-20 pb-10" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Brand */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <img src={logo} alt="Gupta Enterprises logo" className="h-14 w-14 rounded-full object-cover ring-1 ring-[#40916c]/15" />
              <div className="leading-none text-left">
                <p className="font-display font-semibold text-lg text-[#2d6a4f]">Gupta Enterprises</p>
                <p className="font-body text-[11px] tracking-[0.25em] uppercase text-[#40916c]/70 mt-1">Vasavi Gold</p>
              </div>
            </div>
            <p className="font-body text-[#5b6b62] text-sm leading-relaxed mt-6 max-w-sm">
              {t("footer.brandDesc")}
            </p>
            <div className="flex items-center gap-3 mt-7">
              <a
                href="https://www.instagram.com/guptaenterprises_chemudulanka?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw=="
                target="_blank"
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-full border border-[#40916c]/20 flex items-center justify-center text-[#40916c] hover:bg-[#40916c] hover:text-white transition-all"
                data-testid="footer-social-instagram"
                aria-label="Instagram"
              >
                <Instagram size={17} />
              </a>
              <a
                href="#contact"
                onClick={(e) => { e.preventDefault(); go("contact"); }}
                className="h-10 w-10 rounded-full border border-[#40916c]/20 flex items-center justify-center text-[#40916c] hover:bg-[#40916c] hover:text-white transition-all"
                data-testid="footer-social-facebook"
                aria-label="Facebook"
              >
                <Facebook size={17} />
              </a>
              <a
                href="#contact"
                onClick={(e) => { e.preventDefault(); go("contact"); }}
                className="h-10 w-10 rounded-full border border-[#40916c]/20 flex items-center justify-center text-[#40916c] hover:bg-[#40916c] hover:text-white transition-all"
                data-testid="footer-social-linkedin"
                aria-label="LinkedIn"
              >
                <Linkedin size={17} />
              </a>
            </div>
          </div>

          {/* Quick links */}
          <div className="md:col-span-3">
            <p className="eyebrow mb-5">{t("footer.quickLinks")}</p>
            <ul className="space-y-3">
              {navLinks.map((l) => (
                <li key={l.id}>
                  <button
                    onClick={() => go(l.id)}
                    className="font-body text-sm text-[#3a4a41] hover:text-[#40916c] transition-colors"
                    data-testid={`footer-link-${l.id}`}
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <p className="eyebrow mb-5">{t("footer.contactTitle")}</p>
            <ul className="space-y-4 font-body text-sm text-[#3a4a41]">
              <li className="flex items-start gap-3">
                <MapPin size={17} className="text-[#40916c] mt-0.5 shrink-0" />
                <span>{t("footer.address")}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={17} className="text-[#40916c] shrink-0" />
                <a href={`tel:${CONTACT.phoneDial}`} className="hover:text-[#40916c]">{CONTACT.phoneDisplay}</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={17} className="text-[#40916c] shrink-0" />
                <a href={`mailto:${CONTACT.email}`} className="hover:text-[#40916c] break-all">{CONTACT.email}</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[#40916c]/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-body text-xs text-[#5b6b62]">
            © {new Date().getFullYear()} Gupta Enterprises. {t("footer.rights")}
          </p>
          <p className="font-body text-xs text-[#40916c]">Chemudulanka · Andhra Pradesh · Since 1975</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
